/**
 * `/progetto/gantt` — la timeline a piena pagina, e la versione che si stampa.
 *
 * Ci si arriva **cliccando il Gantt** nella Panoramica (scelta di Enrico,
 * 21-08-2026): niente voce di navigazione in più per una cosa che si guarda di
 * rado. Perché si scopra, la card nella Panoramica dichiara di essere
 * cliccabile — i moduli non si trovavano proprio perché nessuno lo diceva.
 *
 * ## La stampa
 *
 * `window.print()` con le utility `print:` di Tailwind, come già in
 * `M2/ListaPiano.tsx`: nessuna libreria PDF. In orizzontale via `@page`, perché
 * una timeline di sei mesi in verticale non si legge.
 *
 * L'intestazione di stampa (nome progetto + data) esiste **solo** su carta: a
 * schermo quelle informazioni sono già nella pagina, su un foglio che gira per
 * l'ufficio no — e un Gantt stampato senza data è un Gantt di cui nessuno sa
 * più se è quello buono.
 */

import { ArrowLeft, Printer } from "lucide-react";
import { Link } from "react-router-dom";

import { Gantt } from "@/components/Gantt";
import { PageContainer } from "@/components/PageContainer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAvanzamento } from "@/hooks/useAvanzamento";
import { useGantt } from "@/hooks/useGantt";
import { useModuli } from "@/hooks/useModuli";

export function GanttPage() {
  const { data: elementi = [], isLoading } = useGantt();
  const { data: moduli = [] } = useModuli();
  const { data: avanzamento } = useAvanzamento();

  const oggi = new Date().toLocaleDateString("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <PageContainer>
      <style>{`@media print { @page { size: A4 landscape; margin: 12mm; } }`}</style>

      <Link
        to="/progetto"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit print:hidden"
      >
        <ArrowLeft className="size-3.5" />
        Stato del progetto
      </Link>

      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Progetto Mister Pet
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Timeline
          </h1>
          <p className="text-sm text-muted-foreground">
            Aggiornata al {oggi}
            {avanzamento && ` · avanzamento ${avanzamento.totale}%`}
          </p>
        </div>
        <Button variant="outline" onClick={() => window.print()} className="print:hidden">
          <Printer className="size-4" />
          Stampa
        </Button>
      </header>

      {isLoading || elementi.length === 0 || moduli.length === 0 ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Gantt elementi={elementi} moduli={moduli} variante="disteso" />
      )}
    </PageContainer>
  );
}
